import { useState, useEffect, useMemo } from 'react';

// Live, ticking T-/T+ countdown for the launch card. Owns its own tick
// state so re-renders are scoped to the countdown text alone -- the parent
// LaunchCard's other fields stay stable. Format adapts to scale (mission
// control style): days+hours far out, full HH:MM:SS inside one day, MM:SS
// inside an hour, raw seconds in the final minute. Color escalates from
// amber to rose to red as the clock approaches T-0; pulses inside T-60s.

const pad = (n) => String(Math.floor(n)).padStart(2, '0');

function formatCountdown(deltaMs) {
  const past = deltaMs < 0;
  const total = Math.floor(Math.abs(deltaMs) / 1000);
  const days  = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins  = Math.floor((total % 3600) / 60);
  const secs  = total % 60;

  if (past) {
    if (total < 3600)  return `T+${pad(mins)}:${pad(secs)}`;
    if (total < 43200) return `T+${pad(hours)}:${pad(mins)}`;
    return 'LIFTED OFF';
  }

  if (days > 7)   return `T-${days}d ${pad(hours)}h`;
  if (days >= 1)  return `T-${days}d ${pad(hours)}:${pad(mins)}`;
  if (hours >= 1) return `T-${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  if (mins >= 1)  return `T-${pad(mins)}:${pad(secs)}`;
  return `T-${pad(secs)}`;
}

function styleForDelta(deltaMs, status) {
  // Status-overrides take precedence over time-based color
  if (status === 'Hold') return { color: 'var(--amber)', weight: 800, pulse: false };
  if (status === 'Failure' || status === 'Partial Failure') {
    return { color: 'var(--red)', weight: 800, pulse: false };
  }
  // Post-launch: bright green
  if (deltaMs < 0) return { color: 'var(--green)', weight: 700, pulse: false };

  // Pre-launch: escalating urgency
  const s = deltaMs / 1000;
  if (s < 60)   return { color: 'var(--red)', weight: 900, pulse: true };
  if (s < 600)  return { color: 'var(--rose)', weight: 900, pulse: false };
  if (s < 3600) return { color: 'var(--amber)', weight: 800, pulse: false };
  return { color: 'var(--amber)', weight: 900, pulse: false };
}

export default function LaunchCountdown({ net, status }) {
  const targetMs = useMemo(() => {
    if (!net) return null;
    const t = new Date(net).getTime();
    return Number.isFinite(t) ? t : null;
  }, [net]);

  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (targetMs == null) return undefined;
    // Don't tick during operational holds -- freeze on whatever value we last
    // showed. The user re-engages with the countdown when status flips back.
    if (status === 'Hold') return undefined;
    // 1Hz everywhere. One setInterval per visible launch card is free, and
    // a single rate avoids the boundary-detection bugs that come with
    // adaptive intervals (interval switches mid-flight, missed seconds, etc).
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs, status]);

  // NET missing -- show placeholder, no countdown
  if (targetMs == null) {
    return (
      <span style={{
        fontSize: 16, fontWeight: 700, fontFamily: 'var(--mono)',
        color: 'var(--txt-mute)', letterSpacing: 1, marginBottom: 8, display: 'block',
        fontVariantNumeric: 'tabular-nums',
      }}>NET TBD</span>
    );
  }

  const delta = targetMs - now;
  const text = formatCountdown(delta);
  const { color, weight, pulse } = styleForDelta(delta, status);

  return (
    <span style={{
      fontSize: 16,
      fontWeight: weight,
      fontFamily: 'var(--mono)',
      color,
      letterSpacing: 1,
      marginBottom: 8,
      display: 'block',
      // tabular-nums prevents the line from jiggling as digit widths change
      fontVariantNumeric: 'tabular-nums',
      animation: pulse ? 'launchPulse 1s ease-in-out infinite' : 'none',
      // Subtle glow at terminal count for that mission-control feel
      textShadow: pulse ? '0 0 8px rgba(224, 90, 90, 0.7)' : 'none',
    }}>{text}</span>
  );
}
